import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Button from "@mui/material/Button";
import Table from "@mui/material/Table";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import Typography from "@mui/material/Typography";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";
import AddIcon from "@mui/icons-material/Add";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import { PageHeader } from "@/components/common/PageHeader";
import { QueryState } from "@/components/common/AsyncState";
import { StatusChip } from "@/components/common/StatusChip";
import { useAuth } from "@/auth/useAuth";
import { useBranch } from "@/auth/BranchContext";
import { fetchGroups } from "@/api/groups";
import { fetchBranches, fetchSubjects } from "@/api/branches";
import { fetchTeachers } from "@/api/teachers";
import { DAYSET_LABELS, GROUP_STATUS_LABELS, daysToDayset } from "@/types/enums";
import type { Dayset, GroupStatus } from "@/types/enums";
import { formatMoney, formatPercent } from "@/utils/format";
import type { Group } from "@/types/group";
import { GroupFormDialog } from "./GroupFormDialog";

function fillColor(group: Group): "error" | "success" | "warning" | "default" {
  const capacity = (group.studentsCount ?? 0) + (group.seatsLeft ?? 0);
  if (!capacity) return "default";
  const fill = (group.studentsCount ?? 0) / capacity;
  if (fill >= 0.9) return "error";
  if (fill >= 0.6) return "success";
  return "warning";
}

const VIEW_STORAGE_KEY = "proschool-groups-view";

function initialViewMode(): "table" | "cards" {
  try {
    const saved = localStorage.getItem(VIEW_STORAGE_KEY);
    return saved === "table" ? "table" : "cards";
  } catch {
    return "cards";
  }
}

export function GroupsListPage() {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { effectiveBranchId } = useBranch();
  const canEdit = hasPermission("groups.edit");
  const canSeeFinance = hasPermission("fin.debts");
  const [branchId, setBranchId] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [subjectId, setSubjectId] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [dayset, setDayset] = useState("");
  const [formOpen, setFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [view, setView] = useState<"table" | "cards">(initialViewMode);

  const { data: branches } = useQuery({ queryKey: ["branches"], queryFn: fetchBranches });
  const { data: subjects } = useQuery({ queryKey: ["subjects"], queryFn: fetchSubjects });
  const { data: teachers } = useQuery({
    queryKey: ["teachers", "filter"],
    queryFn: () => fetchTeachers({ limit: 200 }),
  });
  const { data, isLoading, error } = useQuery({
    queryKey: ["groups", { branchId: effectiveBranchId || branchId, status }],
    queryFn: () => fetchGroups({ branchId: effectiveBranchId || branchId || undefined, status: status || undefined, limit: 100 }),
  });

  const allGroups = data?.items ?? [];
  const groups = allGroups.filter((g) => {
    if (search) {
      const q = search.toLowerCase();
      if (!g.code.toLowerCase().includes(q) && !(g.subjectName ?? "").toLowerCase().includes(q)) return false;
    }
    if (subjectId && g.subjectId !== subjectId) return false;
    if (teacherId && g.teacherId !== teacherId) return false;
    if (dayset && daysToDayset(g.days ?? []) !== dayset) return false;
    return true;
  });
  const filtersApplied = Boolean(search || subjectId || teacherId || dayset || status || branchId);

  function handleViewChange(_: unknown, next: "table" | "cards" | null) {
    if (!next) return;
    setView(next);
    try {
      localStorage.setItem(VIEW_STORAGE_KEY, next);
    } catch {
      /* ignore storage errors */
    }
  }

  return (
    <>
      <PageHeader
        title="Группы"
        subtitle="Учебные группы центра"
        actions={
          canEdit && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setFormOpen(true)}>
              Новая группа
            </Button>
          )
        }
      />

      <Paper className="crm-page-filter" variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }}>
          <TextField select label="Филиал" size="small" value={branchId} onChange={(e) => setBranchId(e.target.value)} sx={{ minWidth: 200 }}>
            <MenuItem value="">Все филиалы</MenuItem>
            {(branches ?? []).map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            size="small"
            label="Поиск"
            placeholder="Код или предмет"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 190 }}
          />
          <TextField select label="Предмет" size="small" value={subjectId} onChange={(e) => setSubjectId(e.target.value)} sx={{ minWidth: 170 }}>
            <MenuItem value="">Все предметы</MenuItem>
            {(subjects ?? []).filter((x) => x.active).map((x) => (
              <MenuItem key={x.id} value={x.id}>
                {x.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Преподаватель" size="small" value={teacherId} onChange={(e) => setTeacherId(e.target.value)} sx={{ minWidth: 190 }}>
            <MenuItem value="">Все преподаватели</MenuItem>
            {(teachers?.items ?? []).map((t) => (
              <MenuItem key={t.id} value={t.id}>
                {t.fullName}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Статус" size="small" value={status} onChange={(e) => setStatus(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Любой статус</MenuItem>
            {(Object.keys(GROUP_STATUS_LABELS) as GroupStatus[]).map((key) => (
              <MenuItem key={key} value={key}>
                {GROUP_STATUS_LABELS[key]}
              </MenuItem>
            ))}
          </TextField>
          <TextField select label="Дни" size="small" value={dayset} onChange={(e) => setDayset(e.target.value)} sx={{ minWidth: 160 }}>
            <MenuItem value="">Все дни</MenuItem>
            {(Object.keys(DAYSET_LABELS) as Dayset[]).map((key) => (
              <MenuItem key={key} value={key}>
                {DAYSET_LABELS[key]}
              </MenuItem>
            ))}
          </TextField>
          {filtersApplied && (
            <Button
              size="small"
              onClick={() => {
                setSearch("");
                setSubjectId("");
                setTeacherId("");
                setDayset("");
                setStatus("");
                setBranchId("");
              }}
            >
              Сбросить
            </Button>
          )}
          <ToggleButtonGroup size="small" exclusive value={view} onChange={handleViewChange}>
            <ToggleButton value="cards" aria-label="Карточки">
              <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />
              Карточки
            </ToggleButton>
            <ToggleButton value="table" aria-label="Таблица">
              <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
              Таблица
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
      </Paper>

      <Paper className="crm-data-panel" variant="outlined">
        <QueryState isLoading={isLoading} error={error}>
          {view === "cards" ? (
            groups.length === 0 ? (
              <Typography p={3} color="text.secondary" textAlign="center">
                Группы не найдены.
              </Typography>
            ) : (
              <Grid container spacing={2} p={2}>
                {groups.map((group) => (
                  <Grid item xs={12} sm={6} md={4} key={group.id}>
                    <Card variant="outlined" sx={{ height: "100%" }}>
                      <CardContent>
                        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
                          <Typography fontWeight={700} sx={{ cursor: "pointer" }} onClick={() => navigate(`/groups/${group.id}`)}>
                            {group.code}
                          </Typography>
                          <Chip
                            size="small"
                            color={fillColor(group)}
                            label={`${group.studentsCount ?? 0}${(group.studentsCount ?? 0) + (group.seatsLeft ?? 0) ? "/" + ((group.studentsCount ?? 0) + (group.seatsLeft ?? 0)) : ""} чел.`}
                          />
                        </Stack>
                        <Typography variant="body2" mb={0.5}>
                          {group.subjectName ?? group.subjectId} · {group.level}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" display="block">
                          {group.teacherName ?? "без преподавателя"}
                        </Typography>
                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={1} mb={1.5}>
                          <StatusChip label={GROUP_STATUS_LABELS[group.status]} />
                          <Chip size="small" variant="outlined" label={(daysToDayset(group.days) && DAYSET_LABELS[daysToDayset(group.days)!]) ?? "—"} />
                          <Chip size="small" variant="outlined" label={group.timeSlot} />
                          <Chip size="small" variant="outlined" label={group.roomName ?? "—"} />
                          <Chip size="small" variant="outlined" label={group.branchName ?? "—"} />
                        </Stack>
                        <Grid container spacing={1} mb={1.5}>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Посещаемость
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {formatPercent(group.attendancePercent ?? 0)}
                            </Typography>
                          </Grid>
                          <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Средний балл
                            </Typography>
                            <Typography variant="body2" fontWeight={600}>
                              {group.averageGrade ? group.averageGrade.toFixed(1) : "—"}
                            </Typography>
                          </Grid>
                          {canSeeFinance && <Grid item xs={4}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              Долг группы
                            </Typography>
                            <Typography variant="body2" fontWeight={600} color={(group.debt ?? 0) > 0 ? "error.main" : "success.main"}>
                              {formatMoney(group.debt ?? 0)}
                            </Typography>
                          </Grid>}
                        </Grid>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Button size="small" variant="contained" onClick={() => navigate(`/journal?groupId=${group.id}`)}>
                            Журнал
                          </Button>
                          <Button size="small" onClick={() => navigate(`/groups/${group.id}`)}>
                            Открыть
                          </Button>
                          {canEdit && (
                            <Button size="small" color="inherit" onClick={() => setEditingGroup(group)}>
                              Изменить
                            </Button>
                          )}
                          <Typography variant="caption" color="text.secondary" ml="auto">
                            {group.lessonsCount ?? 0} уроков проведено
                          </Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Код</TableCell>
                  <TableCell>Предмет</TableCell>
                  <TableCell>Уровень</TableCell>
                  <TableCell>Преподаватель</TableCell>
                  <TableCell>Кабинет</TableCell>
                  <TableCell>Дни</TableCell>
                  <TableCell align="right">Время</TableCell>
                  <TableCell align="right">Учеников</TableCell>
                  <TableCell>Статус</TableCell>
                  <TableCell align="right">Посещ.</TableCell>
                  <TableCell align="right">Балл</TableCell>
                  {canSeeFinance && <TableCell align="right">Долг</TableCell>}
                  <TableCell align="right">Уроков</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {groups.map((group) => (
                  <TableRow key={group.id} hover>
                    <TableCell sx={{ cursor: "pointer", fontWeight: 700 }} onClick={() => navigate(`/groups/${group.id}`)}>
                      {group.code}
                    </TableCell>
                    <TableCell>{group.subjectName ?? group.subjectId}</TableCell>
                    <TableCell>{group.level}</TableCell>
                    <TableCell>{group.teacherName ?? "—"}</TableCell>
                    <TableCell>{group.roomName ?? "—"}</TableCell>
                    <TableCell>{(daysToDayset(group.days) && DAYSET_LABELS[daysToDayset(group.days)!]) ?? "—"}</TableCell>
                    <TableCell align="right">{group.timeSlot}</TableCell>
                    <TableCell align="right">
                      {group.studentsCount ?? 0}
                      {(group.studentsCount ?? 0) + (group.seatsLeft ?? 0) ? "/" + ((group.studentsCount ?? 0) + (group.seatsLeft ?? 0)) : ""}
                    </TableCell>
                    <TableCell>
                      <StatusChip label={GROUP_STATUS_LABELS[group.status]} />
                    </TableCell>
                    <TableCell align="right">{group.attendancePercent ? formatPercent(group.attendancePercent) : "—"}</TableCell>
                    <TableCell align="right">{group.averageGrade ? group.averageGrade.toFixed(1) : "—"}</TableCell>
                    {canSeeFinance && <TableCell align="right">
                      {(group.debt ?? 0) > 0 ? <b style={{ color: "var(--mui-palette-error-main)" }}>{formatMoney(group.debt ?? 0)}</b> : "0"}
                    </TableCell>}
                    <TableCell align="right">{group.lessonsCount ?? 0}</TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Button size="small" variant="contained" onClick={() => navigate(`/journal?groupId=${group.id}`)}>
                        Журнал
                      </Button>
                      {canEdit && (
                        <Button size="small" color="inherit" onClick={() => setEditingGroup(group)}>
                          Изменить
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {groups.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={14} align="center">
                      Группы не найдены.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}
        </QueryState>
      </Paper>

      {canEdit && <GroupFormDialog open={formOpen} onClose={() => setFormOpen(false)} />}
      {canEdit && editingGroup && <GroupFormDialog open={Boolean(editingGroup)} onClose={() => setEditingGroup(null)} group={editingGroup} />}
    </>
  );
}
